// =============================================================================
// 2026-06-21 (user report): SATU humanizer aktivitas untuk semua permukaan —
// tab Riwayat Aktivitas DAN kartu "Aktivitas Terbaru" di Ringkasan Monitoring.
// Sebelumnya Ringkasan menampilkan mentah "AUDIT update" dengan waktu/sumber "—".
//
// Aturan: tampilan utama TIDAK BOLEH menampilkan nama field internal
// (snake_case) atau JSON mentah; fallback: underscore → spasi.
// =============================================================================
import { fmtDate, formatIDR } from "@/lib/constants";

export const ACTION_LABEL = {
  create: "Membuat data baru",
  update: "Memperbarui data",
  delete: "Menghapus data",
  close: "Menutup proyek",
  reopen: "Membuka kembali proyek",
  cancel_by_owner: "WO dibatalkan oleh pemilik",
  reopen_cancelled: "Pembatalan WO dikembalikan (WO aktif kembali)",
  restore: "Memulihkan data",
  soft_delete: "Mengarsipkan data",
  purge: "Menghapus permanen",
  override_change: "Mengubah data (override)",
  access_grant: "Memberikan akses proyek",
  access_revoke: "Mencabut akses proyek",
  grant: "Memberikan akses",
  revoke: "Mencabut akses",
  "expense.create": "Mencatat pengeluaran",
  login: "Masuk aplikasi",
  logout: "Keluar aplikasi",
};

export const ACTION_TONE = {
  create: "bg-emerald-50 border-emerald-300 text-emerald-700",
  delete: "bg-rose-50 border-rose-300 text-rose-700",
  purge: "bg-rose-50 border-rose-300 text-rose-700",
  cancel_by_owner: "bg-slate-100 border-slate-300 text-slate-600",
  close: "bg-emerald-50 border-emerald-300 text-emerald-700",
};

export const KIND_LABEL = {
  project: "Manajemen Proyek", active_project: "Manajemen Proyek",
  working_order: "Working Order", wo: "Working Order",
  finance: "Laporan Keuangan", income: "Laporan Keuangan", expense: "Laporan Keuangan",
  petty_cash: "Weekly Petty Cash", nav_order_setting: "Settings",
  access_control: "Kontrol Akses", auth: "Autentikasi",
  security: "Keamanan", parent_maintenance_lifecycle: "Manajemen Proyek",
  role_preset: "Preset Peran", security_session_setting: "Keamanan",
};

// Nama field teknis → Bahasa Indonesia (tampilan utama tidak boleh menampilkan
// nama internal; fallback: underscore diubah jadi spasi).
export const FIELD_LABEL = {
  status: "Status", client: "Client", contract_value: "Nilai Kontrak",
  contract_number: "Nomor Kontrak/PO", po_number: "Nomor PO", wo_number: "Nomor WO",
  po_date: "Tanggal PO", po_value: "Nilai PO", wo_date: "Tanggal WO",
  wo_title: "Judul WO", work_description: "Deskripsi Pekerjaan",
  pic_name: "PIC", pic_finance_name: "PIC Keuangan", project_name: "Nama Proyek",
  project_type: "Jenis Proyek", project_code: "Kode Proyek",
  start_date: "Tanggal Mulai", end_date: "Tanggal Selesai",
  vo_start_date: "Tanggal Mulai VO", vo_end_date: "Tanggal Selesai VO",
  work_start_date: "Mulai Pengerjaan", project_done_date: "Selesai Fisik",
  invoice_sent_date: "Invoice Terkirim", invoice_date: "Tanggal Invoice",
  invoice_no: "Nomor Invoice", site_survey_date: "Site Survey",
  sph_submit_date: "SPH Terkirim", physical_progress_pct: "Progress Pekerjaan",
  cancelled_reason: "Alasan Pembatalan", delete_reason: "Alasan Hapus",
  is_ppn_exempt: "Status PPN", retention_pct: "Retensi (%)",
  retention_period_days: "Masa Retensi (hari)", retention_type: "Tipe Retensi",
  retention_amount: "Nilai Retensi", retention_released_amount: "Retensi Dicairkan",
  retention_due_date: "Jatuh Tempo Retensi",
  pph_amount: "Potongan PPh", ppn_amount: "PPN", amount: "Nilai", date: "Tanggal",
  waiting_payment: "Status Pembayaran", payment_receipts: "Riwayat Pembayaran",
  payment_promises: "Janji Bayar", is_urgent: "Kategori WO", stage: "Tahap",
  perlu_tindakan_manual: "Tindakan Berikutnya",
  sph_submissions: "Daftar SPH", invoice_entries: "Daftar Invoice",
  income_entries: "Daftar Penerimaan", expense_entries: "Daftar Pengeluaran",
  monthly_progress: "Progress Bulanan", client_contacts: "Kontak Client",
  project_documents: "Dokumen Proyek", site_contact: "Kontak Lapangan",
  scope_of_work: "Lingkup Pekerjaan", team_cc: "Tim CC", assigned_to: "Ditugaskan ke",
  due_date: "Jatuh Tempo", priority: "Prioritas", description: "Deskripsi",
  location: "Lokasi", province: "Provinsi", city: "Kota/Kabupaten",
  bank_name: "Bank", account_no: "Nomor Rekening", notes: "Catatan",
  google_plus_code: "Google Plus Code", is_outlet: "Tipe Outlet",
  payment_terms: "Termin Pembayaran", ppn_rate: "Tarif PPN",
};

export const VALUE_LABEL = {
  active: "Aktif", completed: "Selesai", cancelled: "Dibatalkan",
  cancelled_by_owner: "Dibatalkan Owner", in_progress: "Sedang Dikerjakan",
  approved: "Disetujui", submitted: "Diajukan", pending: "Menunggu",
  rejected: "Ditolak", draft: "Draf", verified: "Terverifikasi",
  waiting: "Menunggu Pembayaran", paid: "Lunas", partial: "Terbayar Sebagian",
  sent: "Terkirim", open: "Terbuka", close: "Ditutup",
  new_project: "Proyek Baru", variation_order: "Variation Order (VO)",
  working_order: "Working Order (WO)", internal: "Internal",
  inside: "Dipotong dari pembayaran", outside: "Ditagihkan terpisah",
  urgent: "Urgent Case", non_urgent: "Non-Urgent Case",
  true: "Ya", false: "Tidak",
};

// Label kunci di dalam item objek/array (mis. item SPH).
export const ITEM_KEY_LABEL = {
  version: "Versi", versi: "Versi", status: "Status", date: "Tanggal",
  submitted_date: "Tanggal Pengajuan", tanggal: "Tanggal", note: "Catatan",
  amount: "Nilai", contract_value: "Nilai Kontrak", name: "Nama",
  project_name: "Nama Proyek", client: "Client", description: "Deskripsi",
  month: "Bulan", pct: "Persentase", file_name: "Berkas", phone: "Telepon",
  email: "Email", bank_name: "Bank", account_no: "No. Rekening",
};

export const humanKey = (k) => ITEM_KEY_LABEL[k] || FIELD_LABEL[k] || String(k || "").replace(/_/g, " ");
const isDateKey = (k) => /date|tanggal|_at$/.test(k || "");
const isMoneyKey = (k) => /contract_value|amount|harga|nilai|retention_pct/.test(k || "");

/** Nilai primitif → teks ramah. Objek/array TIDAK di-stringify di sini. */
export const fmtPrimitive = (key, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Ya" : "Tidak";
  if (typeof v === "object") return null; // ditangani humanizer objek/array
  const s = String(v);
  if (VALUE_LABEL[s]) return VALUE_LABEL[s];
  if (isDateKey(key) && /^\d{4}-\d{2}-\d{2}/.test(s)) return fmtDate(s);
  if (isMoneyKey(key) && !isNaN(Number(v))) return formatIDR(Number(v));
  return s;
};

/** Satu item objek → frasa pendek. Mis. SPH: "SPH versi 2 tanggal 5 Sep 2026
 *  dengan status Disetujui". TIDAK pernah JSON mentah. */
export function humanItem(contextField, item) {
  if (item === null || item === undefined) return "item kosong";
  if (typeof item !== "object") return fmtPrimitive(contextField, item) ?? String(item);
  const parts = [];
  const noun = contextField === "sph_submissions" ? "SPH"
    : contextField === "project_documents" ? "Dokumen"
    : contextField === "client_contacts" ? "Kontak"
    : contextField === "monthly_progress" ? "Progress"
    : contextField === "scope_of_work" ? "Pekerjaan"
    : null;
  const name = item.project_name || item.wo_number || item.name || item.title || item.file_name || item.label;
  if (noun && (item.version || item.versi)) parts.push(`${noun} versi ${item.version || item.versi}`);
  else if (noun) parts.push(noun);
  if (name) parts.push(String(name));
  const dateV = item.date || item.tanggal || item.submitted_date || item.month;
  if (dateV) parts.push(isDateKey("date") && /^\d{4}-\d{2}-\d{2}/.test(String(dateV)) ? `tanggal ${fmtDate(dateV)}` : `tanggal ${dateV}`);
  if (item.status) parts.push(`dengan status ${VALUE_LABEL[item.status] || item.status}`);
  const amt = item.amount ?? item.contract_value;
  if (amt !== undefined && amt !== null && !isNaN(Number(amt))) parts.push(`senilai ${formatIDR(Number(amt))}`);
  if (item.pct !== undefined) parts.push(`${item.pct}%`);
  if (parts.length === 0) {
    // Objek tanpa kunci dikenal: rangkum pasangan kunci-nilai primitif (maks 3).
    const kv = Object.entries(item)
      .filter(([, v]) => typeof v !== "object" || v === null)
      .slice(0, 3)
      .map(([k, v]) => `${humanKey(k)}: ${fmtPrimitive(k, v) ?? "—"}`);
    return kv.length ? kv.join(", ") : "rincian tersimpan";
  }
  return parts.join(" ");
}

/** Diff dua objek (kunci per kunci) → kalimat "Label: lama → baru". */
export function diffObject(fieldLabel, oldObj, newObj) {
  const keys = Array.from(new Set([...Object.keys(oldObj || {}), ...Object.keys(newObj || {})]))
    .filter((k) => !["id", "_id", "created_at", "updated_at", "created_by"].includes(k));
  const lines = [];
  for (const k of keys) {
    const ov = (oldObj || {})[k];
    const nv = (newObj || {})[k];
    if (JSON.stringify(ov) === JSON.stringify(nv)) continue;
    if (typeof ov === "object" || typeof nv === "object") {
      if (Array.isArray(ov) || Array.isArray(nv)) {
        lines.push(...diffArray(`${fieldLabel} › ${humanKey(k)}`, ov || [], nv || []));
      } else {
        lines.push(...diffObject(`${fieldLabel} › ${humanKey(k)}`, ov || {}, nv || {}));
      }
      continue;
    }
    lines.push(`${humanKey(k)}: ${fmtPrimitive(k, ov) ?? "—"} → ${fmtPrimitive(k, nv) ?? "—"}`);
  }
  return lines;
}

/** Diff dua array per indeks → kalimat Menambahkan/Menghapus/Mengubah. */
export function diffArray(fieldLabel, oldArr, newArr) {
  const o = Array.isArray(oldArr) ? oldArr : [];
  const n = Array.isArray(newArr) ? newArr : [];
  const lines = [];
  const shared = Math.min(o.length, n.length);
  for (let i = 0; i < shared; i++) {
    if (JSON.stringify(o[i]) === JSON.stringify(n[i])) continue;
    if (typeof o[i] === "object" && typeof n[i] === "object" && o[i] && n[i]) {
      const sub = diffObject(humanItem(fieldLabel, n[i]), o[i], n[i]);
      if (sub.length) lines.push(`Mengubah ${humanItem(fieldLabel, n[i])} — ${sub.join("; ")}`);
    } else {
      lines.push(`Mengubah ${fieldLabel} urutan ${i + 1}: ${fmtPrimitive(fieldLabel, o[i]) ?? humanItem(fieldLabel, o[i])} → ${fmtPrimitive(fieldLabel, n[i]) ?? humanItem(fieldLabel, n[i])}`);
    }
  }
  for (let i = shared; i < n.length; i++) lines.push(`Menambahkan ${humanItem(fieldLabel, n[i])}`);
  for (let i = shared; i < o.length; i++) lines.push(`Menghapus ${humanItem(fieldLabel, o[i])}`);
  if (lines.length === 0 && o.length !== n.length) {
    lines.push(`${fieldLabel}: ${o.length} item → ${n.length} item`);
  }
  return lines;
}

/** Bangun ringkasan perubahan: { short, lines[] } — tanpa JSON/nama internal. */
export function summarize(a) {
  const target = a.target_label || a.project_name || null;
  const fieldLabel = FIELD_LABEL[a.field] || (a.field ? String(a.field).replace(/_/g, " ") : null);
  if (a.action === "create") {
    const nv = a.new_value && typeof a.new_value === "object" ? a.new_value : null;
    const detail = nv ? humanItem(a.field, nv) : null;
    const short = `Membuat data baru${detail && detail !== "rincian tersimpan" ? `: ${detail}` : target ? `: ${target}` : ""}`;
    return { short, lines: [short] };
  }
  if (a.action === "delete") {
    const reason = a.delete_reason ? ` — alasan: ${a.delete_reason}` : "";
    const short = `Menghapus data${target ? `: ${target}` : ""}${reason}`;
    return { short, lines: [short] };
  }
  if (a.field && a.field !== "__create__" && a.field !== "__delete__") {
    const ov = a.old_value, nv = a.new_value;
    if (Array.isArray(ov) || Array.isArray(nv)) {
      const lines = diffArray(fieldLabel, ov || [], nv || []);
      const short = lines.length === 0 ? `${fieldLabel} diperbarui`
        : lines.length === 1 ? lines[0]
        : `${lines[0]} (+${lines.length - 1} perubahan lain)`;
      return { short, lines: lines.length ? lines : [`${fieldLabel} diperbarui`] };
    }
    if ((ov && typeof ov === "object") || (nv && typeof nv === "object")) {
      const lines = diffObject(fieldLabel, ov || {}, nv || {});
      const short = lines.length === 0 ? `${fieldLabel} diperbarui`
        : lines.length === 1 ? lines[0]
        : `${lines[0]} (+${lines.length - 1} perubahan lain)`;
      return { short, lines: lines.length ? lines : [`${fieldLabel} diperbarui`] };
    }
    // 2026-06-21: record ringkas (mis. kartu Aktivitas Terbaru) tidak membawa
    // old/new value — jangan tampilkan "— → —", cukup "Label diperbarui".
    if (ov === undefined && nv === undefined) {
      const s0 = `${fieldLabel} diperbarui`;
      return { short: s0, lines: [s0] };
    }
    const s = `${fieldLabel}: ${fmtPrimitive(a.field, ov)} → ${fmtPrimitive(a.field, nv)}`;
    return { short: s, lines: [s] };
  }
  const s = `${ACTION_LABEL[a.action] || "Memperbarui data"}${target ? `: ${target}` : ""}`;
  return { short: s, lines: [s] };
}

export const fmtWaktu = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
